import {
  GpsOff,
  Pause,
  People,
  PlayArrow,
  Replay,
  Stop,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useEffect, useState, type FC } from "react";
import { useTranslation } from "react-i18next";

import { FramerTooltip } from "@ryde/controls";
import { TOOLTIP_DELAY_BUTTONS, useFormats } from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import {
  JUST_FIRED_EVENT_VISIBLE_MS,
  PLAYBACK_SPEED_OPTIONS,
  RIDE_INVESTIGATION_EVENT_ICON,
  RIDE_INVESTIGATION_SEVERITY_BG,
  RIDE_INVESTIGATION_SEVERITY_COLOR,
} from "../investigation.constants";
import { formatElapsed, formatKmh } from "../investigation.utils";
import { usePlaybackContext } from "../playback/ride-investigation-playback.context";
import { usePlaybackSnapshot } from "../playback/use-playback-snapshot";
import { InvestigationFollowToggle } from "./investigation.follow-toggle";
import { InvestigationTimeline } from "./investigation.timeline";
import {
  SpeedChipStyled,
  TimelineWrapperStyled,
  TransportBarStyled,
  TransportCalloutStyled,
  TransportClockStyled,
  TransportSpacerStyled,
  TransportSpeedGroupStyled,
  TransportStatChipStyled,
} from "./investigation.transport.styled";

/**
 * Bottom "video player" transport: play/pause, restart, speed, live clock, occupancy and the timeline. A ride
 * with no GPS has nothing to replay, so the controls give way to a notice; the timeline still scrubs.
 */
export const InvestigationTransportBar: FC = () => {
  const {
    model,
    isPlaying,
    toggle,
    restart,
    reset,
    speedMultiplier,
    setSpeedMultiplier,
    justFiredEvent,
  } = usePlaybackContext();
  const snapshot = usePlaybackSnapshot();
  const { formatTimeWithSecondsNormalized } = useFormats();
  const { t } = useTranslation();
  const [visibleEventId, setVisibleEventId] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!justFiredEvent) {
      return undefined;
    }

    setVisibleEventId(justFiredEvent.event.id);
    const handle = window.setTimeout(
      () => setVisibleEventId(undefined),
      JUST_FIRED_EVENT_VISIBLE_MS,
    );

    return () => window.clearTimeout(handle);
  }, [justFiredEvent]);

  if (!model) {
    return null;
  }

  const currentMs = snapshot?.tMs ?? model.startMs;
  const absoluteTime = formatTimeWithSecondsNormalized(
    new Date(currentMs).toISOString() as TDateISO,
  );
  const calloutVisible =
    justFiredEvent && justFiredEvent.event.id === visibleEventId;
  const CalloutIcon = justFiredEvent
    ? RIDE_INVESTIGATION_EVENT_ICON[justFiredEvent.event.type]
    : undefined;

  return (
    <TransportBarStyled>
      <div className="transport-row">
        {!model.hasGps && (
          <div className="transport-notice" role="status">
            <GpsOff aria-hidden="true" />
            {t("rideMap.investigation.noTracking")}
          </div>
        )}
        {model.hasGps && (
          <>
            <div className="transport-controls">
              <FramerTooltip
                content={
                  isPlaying
                    ? t("rideMap.investigation.transport.pause")
                    : t("rideMap.investigation.transport.play")
                }
                mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
              >
                <IconButton
                  size="small"
                  color="primary"
                  onClick={toggle}
                  aria-label={t("rideMap.investigation.transport.play")}
                  data-e2e="ride-investigation-play-toggle"
                  sx={{
                    bgcolor: "primary.main",
                    color: rydeColors.white,
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                >
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>
              </FramerTooltip>
              <FramerTooltip
                content={t("rideMap.investigation.transport.reset")}
                mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
              >
                <IconButton
                  size="small"
                  onClick={reset}
                  aria-label={t("rideMap.investigation.transport.reset")}
                  data-e2e="ride-investigation-reset"
                >
                  <Stop />
                </IconButton>
              </FramerTooltip>
              <FramerTooltip
                content={t("rideMap.investigation.transport.restart")}
                mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
              >
                <IconButton
                  size="small"
                  onClick={restart}
                  aria-label={t("rideMap.investigation.transport.restart")}
                >
                  <Replay />
                </IconButton>
              </FramerTooltip>
              <InvestigationFollowToggle />
            </div>

            <TransportSpeedGroupStyled>
              {PLAYBACK_SPEED_OPTIONS.map(option => (
                <SpeedChipStyled
                  key={option}
                  type="button"
                  $active={option === speedMultiplier}
                  onClick={() => setSpeedMultiplier(option)}
                >
                  {option}×
                </SpeedChipStyled>
              ))}
            </TransportSpeedGroupStyled>
          </>
        )}

        {calloutVisible && justFiredEvent && (
          <TransportCalloutStyled
            $color={
              RIDE_INVESTIGATION_SEVERITY_COLOR[justFiredEvent.event.severity]
            }
            $bg={RIDE_INVESTIGATION_SEVERITY_BG[justFiredEvent.event.severity]}
          >
            {CalloutIcon && <CalloutIcon />}
            <span>{t(justFiredEvent.event.labelKey)}</span>
          </TransportCalloutStyled>
        )}

        <TransportSpacerStyled />

        <TransportStatChipStyled>
          <People />
          <span className="stat-value">{snapshot?.occupancy ?? 0}</span>
        </TransportStatChipStyled>

        {snapshot?.speedKmh !== undefined && (
          <TransportStatChipStyled>
            <span className="stat-value">{formatKmh(snapshot.speedKmh)}</span>
          </TransportStatChipStyled>
        )}

        <TransportClockStyled>
          <span className="clock-absolute">{absoluteTime}</span>
          <span className="clock-elapsed">
            {formatElapsed(currentMs - model.startMs)} /{" "}
            {formatElapsed(model.durationMs)}
          </span>
        </TransportClockStyled>
      </div>

      <TimelineWrapperStyled>
        <InvestigationTimeline />
      </TimelineWrapperStyled>
    </TransportBarStyled>
  );
};
